import { TextInput, Button, Heading } from '@pandiafi/design-system'
import classNames from 'classnames'
import { Formik, Form, Field, FieldProps } from 'formik'
import { t } from 'i18next'
import React, { FC, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'

import { LoginInput, loginInputSchema } from '../../../common/validations'
import { formatFormValues } from './utils'
import { initialValues } from './initialValues'
import { FormErrorSection } from './FormErrorSection'
import { SignInContextNotification } from './SignInContextNotification'
import { ResidentAppError } from '../../../common/error'
import { PageTranslationSafeLabel } from '../../components'

import { useAPILogin } from 'api/apiHooks/useAPILogin'
import FormFieldWrapper from 'components/FormField'
import routePath from 'router/routePath'
import { validateFormValues } from 'utils'

interface LoginFormProps {
  error?: string | null
}

export const LoginForm: FC<LoginFormProps> = ({ error: viewError }) => {
  const navigate = useNavigate()
  const [error, setError] = React.useState<LoginFormProps['error']>(null)
  const [successfullyVerifiedMessage, setSuccessfullyVerifiedMessage] =
    React.useState<string | null>(null)
  const [email, setEmail] = React.useState('')

  const { mutate: login, isPending } = useAPILogin({
    onSuccess: () => {
      window.location.replace(routePath.root)
    },
    onError(err) {
      if (err instanceof ResidentAppError) {
        switch (err.errorCode) {
          case 'INVALID_CREDENTIALS':
            setError(t('errors:auth.invalidCredentials'))
            return
          case 'USER_NOT_CONFIRMED':
            navigate(routePath.register, {
              state: { continueRegistration: true, email },
            })
            return
          default:
        }
      }

      setError(t('login:error'))
    },
  })

  useEffect(() => {
    if (viewError) {
      setError(viewError)
    }
  }, [viewError])

  useEffect(() => {
    const warning = localStorage.getItem('loginDevWarning')

    if (warning) {
      // eslint-disable-next-line no-console
      console.warn('[DEV WARNING]', warning)
    }
  }, [])

  const handleRegisterClick = () => {
    navigate(routePath.registerAuth, {
      state: { authType: 'telia' },
    })
  }

  const handleForgotPasswordClick = () => {
    navigate(routePath.forgotPassword)
  }

  const handleValidate = (formValues: LoginInput) => {
    const validationErrors = validateFormValues(
      formatFormValues(formValues),
      loginInputSchema
    )
    return validationErrors
  }

  const handleSubmit = async (values: LoginInput) => {
    setSuccessfullyVerifiedMessage(null)
    setError(null)
    login(values)
  }

  return (
    <Formik
      initialValues={initialValues}
      onSubmit={handleSubmit}
      validate={handleValidate}
      validateOnMount
    >
      {(formik) => (
        <Form className="w-full">
          <Heading element="h1" size="xl" className="mt-6 mb-4">
            {t('login:heading')}
          </Heading>
          <div className={classNames('flex flex-col mb-6')}>
            <Field name="username">
              {({ field, meta }: FieldProps<LoginInput['username']>) => (
                <FormFieldWrapper
                  name={field.name}
                  meta={meta}
                  label={t('login:username')}
                  required
                >
                  <TextInput
                    type="email"
                    {...field}
                    onChange={(event) => {
                      const lowercaseValue = event.target.value.toLowerCase()
                      setEmail(lowercaseValue)
                      formik.setFieldValue('username', lowercaseValue)
                    }}
                    onBlur={(event) => {
                      field.onBlur(event)
                      if (event.target.value === '') {
                        formik.setFieldTouched('username', false)
                      }
                    }}
                  />
                </FormFieldWrapper>
              )}
            </Field>

            <Field name="password">
              {({ field, meta }: FieldProps<LoginInput['password']>) => (
                <FormFieldWrapper
                  name={field.name}
                  meta={meta}
                  label={t('login:password')}
                  required
                >
                  <TextInput
                    {...field}
                    type="password"
                    onChange={(event) => {
                      field.onChange(event)
                    }}
                    onBlur={(event) => {
                      field.onBlur(event)
                      if (event.target.value === '') {
                        formik.setFieldTouched('password', false)
                      }
                    }}
                  />
                </FormFieldWrapper>
              )}
            </Field>
          </div>

          <FormErrorSection error={error} />

          {successfullyVerifiedMessage && (
            <SignInContextNotification text={successfullyVerifiedMessage} />
          )}

          <Button
            className="mb-10 mt-5 w-full"
            disabled={isPending}
            loading={isPending}
            type="submit"
          >
            <PageTranslationSafeLabel>
              {t('login:submit')}
            </PageTranslationSafeLabel>
          </Button>

          <div className="flex flex-col">
            <Button onClick={handleRegisterClick} variant="text">
              <PageTranslationSafeLabel className="underline">
                {t('registration:registerButton')}
              </PageTranslationSafeLabel>
            </Button>

            <Button onClick={handleForgotPasswordClick} variant="text">
              <PageTranslationSafeLabel className="underline">
                {t('login:forgotPasswordButtonText')}
              </PageTranslationSafeLabel>
            </Button>
          </div>
        </Form>
      )}
    </Formik>
  )
}
